/**
 * Header Component
 * 
 * Global navigation header with responsive menu and smooth scrolling navigation.
 * Handles both desktop and mobile layouts with drawer menu for smaller screens.
 * 
 * Interactions:
 * - Uses react-router-dom for navigation between pages
 * - Implements smooth scrolling to page sections via element IDs
 * - Integrates with antd's Drawer component for mobile menu
 * - Uses withTranslation HOC for internationalized menu items
 * - Coordinates with SvgIcon for logo display and Button for CTA
 * - Handles cross-page navigation and same-page scrolling logic
 */

import { useState } from "react";
import { Row, Col, Drawer } from "antd";
import { withTranslation, TFunction } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import Container from "../../common/Container";
import { SvgIcon } from "../../common/SvgIcon";
import { Button } from "../../common/Button";
import {
  HeaderSection,
  LogoContainer,
  Burger,
  NotHidden,
  Menu,
  CustomNavLinkSmall,
  Label,
  Outline,
  Span,
} from "./styles";

const Header = ({ t }: { t: TFunction }) => {
  const [visible, setVisibility] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const toggleButton = () => {
    setVisibility(!visible);
  };

  const MenuItem = () => {
    const scrollTo = (id: string) => {
      // If we're not on the home page, navigate to home first
      if (location.pathname !== '/' && location.pathname !== '/home') {
        history.push(`/#${id}`);
        // Wait for navigation then scroll
        setTimeout(() => {
          const element = document.getElementById(id) as HTMLDivElement;
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
            });
          }
        }, 100);
      } else {
        // We're on home page, just scroll
        const element = document.getElementById(id) as HTMLDivElement;
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
          });
        }
      }
      setVisibility(false);
    };

    const navigateToServices = () => {
      history.push('/services');
      setVisibility(false);
    };

    return (
      <>
        <CustomNavLinkSmall onClick={() => scrollTo("about")}>
          <Span>{t("About")}</Span>
        </CustomNavLinkSmall>
        <CustomNavLinkSmall onClick={() => scrollTo("mission")}>
          <Span>{t("Mission")}</Span>
        </CustomNavLinkSmall>
        <CustomNavLinkSmall onClick={navigateToServices}>
          <Span>{t("Services")}</Span>
        </CustomNavLinkSmall>
        <CustomNavLinkSmall
          style={{ width: "180px" }}
          onClick={() => scrollTo("contact")}
        >
          <Span>
            <Button boxShadow={false}>{t("Contact")}</Button>
          </Span>
        </CustomNavLinkSmall>
      </>
    );
  };

  return (
    <HeaderSection>
      <Container>
        <Row justify="space-between">
          <LogoContainer to="/" aria-label="homepage">
            <div style={{ marginLeft: "-20px" }}>
              <SvgIcon src="wreath.svg" width="80px" height="80px" />
            </div>
            <SvgIcon src="logoac-cropped.svg" width="180px" height="90px" />
          </LogoContainer>
          <NotHidden>
            <MenuItem />
          </NotHidden>
          <Burger onClick={toggleButton}>
            <Outline />
          </Burger>
        </Row>
        <Drawer closable={false} open={visible} onClose={toggleButton}>
          <Col style={{ marginBottom: "2.5rem" }}>
            <Label onClick={toggleButton}>
              <Col span={12}>
                <Menu>Menu</Menu>
              </Col>
              <Col span={12}>
                <Outline />
              </Col>
            </Label>
          </Col>
          <MenuItem />
        </Drawer>
      </Container>
    </HeaderSection>
  );
};

export default withTranslation()(Header);
